import { isSameOriginUrl, openExternalLink } from './navigate';
import { buildUrlFromSettings, resolveUrlTarget } from './routeUrl';
import type {
  PageRouteSettings,
  TaskNavigateOptions,
  TaskPageTarget,
  TaskRouter,
  TaskUtils
} from './types';
import { warnSdkUtilFallback } from './warn';

export function createPageUtils(
  settings: PageRouteSettings,
  router: TaskRouter
): TaskUtils['page'] {
  const pageUrl = (
    target: TaskPageTarget,
    paramsArg?: Record<string, unknown>,
    util: 'page.url' | 'page.navigate' = 'page.url'
  ): string | null => {
    try {
      const { id, params } = resolveUrlTarget(
        target,
        paramsArg,
        `Sdk.utils.${util} requires a page id.`
      );

      const entry = settings?.[id];
      const path = buildUrlFromSettings(entry, 'Page', id, params);

      if (entry?.origin === null) {
        throw new Error(`Page '${id}' does not have an active domain.`);
      }

      return entry?.origin ? `${entry.origin}${path}` : path;
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'The page URL could not be resolved.';
      warnSdkUtilFallback(util, `${reason} Returning null.`);
      return null;
    }
  };

  return {
    url: pageUrl,

    navigate: (target: TaskPageTarget, options?: TaskNavigateOptions) => {
      const url = pageUrl(target, undefined, 'page.navigate');

      if (url === null) {
        return;
      }

      if (options?.openInNewTab) {
        openExternalLink(url);
        return;
      }

      if (!isSameOriginUrl(url)) {
        openExternalLink(url, false);
        return;
      }

      router.visit(url, {
        preserveScroll: options?.preserveScroll ?? false,
        preserveState: options?.preserveState ?? false
      });
    }
  };
}
